import React from "react";
import { NavLink, Link } from "react-router-dom";
import { Home, ShoppingBag, MessageCircle, BookOpen } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useSettings } from "@/context/SettingsContext";
import { resolvePhotoUrl } from "@/lib/api";

function ContactSellerLink({ className, iconClass }) {
    const { whats_app_number } = useSettings();
    if (!whats_app_number) return null;
    const href = `https://wa.me/${whats_app_number}?text=${encodeURIComponent("Hi")}`;
    return (
        <a
            href={href}
            target="_blank"
            rel="noreferrer"
            aria-label="Contact seller"
            title="Contact seller"
            className={className}
        >
            <MessageCircle className={iconClass} strokeWidth={1.5} />
        </a>
    );
}

const items = [
    { to: "/", label: "Shop", icon: Home, testid: "nav-shop" },
    { to: "/checkout", label: "Cart", icon: ShoppingBag, testid: "nav-cart" },
];

// Small logo image shared by desktop & mobile headers
function StoreMark({ size = "h-9 w-9", testid = "store-logo" }) {
    const { logo_url, store_name } = useSettings();
    if (!logo_url) return null;
    return (
        <span
            className={`${size} rounded-xl overflow-hidden bg-surface/60 flex items-center justify-center shrink-0`}
        >
            <img
                data-testid={testid}
                src={resolvePhotoUrl(logo_url)}
                alt={store_name ? `${store_name} logo` : "Store logo"}
                className="w-full h-full object-contain"
            />
        </span>
    );
}

// Mobile: sticky top bar with logo + store name (visible below md)
export function MobileHeader() {
    const { store_name } = useSettings();
    return (
        <header
            data-testid="mobile-top-header"
            className="md:hidden sticky top-0 z-40 glass border-b border-border/40"
        >
            <div className="w-full max-w-md mx-auto px-4 py-3 flex items-center justify-between gap-3">
                <Link to="/" className="flex items-center gap-2 min-w-0">
                    <StoreMark size="h-8 w-8" testid="mobile-store-logo" />
                    <span
                        data-testid="mobile-store-name"
                        className="font-serif text-lg text-foreground truncate"
                    >
                        {store_name}
                    </span>
                </Link>
                <ContactSellerLink
                    className="shrink-0 text-muted-foreground hover:text-foreground transition-colors"
                    iconClass="h-6 w-6"
                />
            </div>
        </header>
    );
}

// Mobile: fixed bottom navigation
export function BottomNav() {
    return (
        <nav
            data-testid="bottom-nav"
            className="md:hidden fixed bottom-0 left-1/2 -translate-x-1/2 z-50 glass border-t border-border/40 pb-safe pt-2 px-8 flex justify-between items-center gap-10 w-full max-w-md"
        >
            {items.map(({ to, label, icon: Icon, testid }) => (
                <NavLink
                    key={to}
                    to={to}
                    end={to === "/"}
                    data-testid={testid}
                    className={({ isActive }) =>
                        `flex flex-col items-center gap-1 text-xs uppercase tracking-[0.15em] transition-colors ${
                            isActive
                                ? "text-primary font-semibold"
                                : "text-muted-foreground hover:text-foreground"
                        }`
                    }
                >
                    <Icon className="h-5 w-5" strokeWidth={1.5} />
                    {label}
                </NavLink>
            ))}
            <a
                href="/how-to-order.html"
                data-testid="nav-how-to-order"
                className="flex flex-col items-center gap-1 text-xs uppercase tracking-[0.15em] text-muted-foreground hover:text-foreground transition-colors"
            >
                <BookOpen className="h-5 w-5" strokeWidth={1.5} />
                How to Order
            </a>
        </nav>
    );
}

// Desktop: sticky top navigation bar
export function TopNav() {
    const { count } = useCart();
    const { store_name } = useSettings();
    return (
        <header
            data-testid="top-nav"
            className="hidden md:block sticky top-0 z-40 glass border-b border-border/40"
        >
            <div className="max-w-7xl mx-auto px-8 py-4 flex items-center justify-between">
                <Link to="/" className="flex items-center gap-3 min-w-0">
                    <StoreMark size="h-10 w-10" testid="desktop-store-logo" />
                    <span
                        data-testid="store-name"
                        className="font-serif text-2xl text-foreground truncate"
                    >
                        {store_name}
                    </span>
                </Link>
                <nav className="flex items-center gap-2">
                    {items.map(({ to, label, testid }) => (
                        <NavLink
                            key={to}
                            to={to}
                            end={to === "/"}
                            data-testid={`top-${testid}`}
                            className={({ isActive }) =>
                                `relative px-4 py-2 rounded-full text-sm transition-colors ${
                                    isActive
                                        ? "bg-foreground text-background"
                                        : "text-foreground hover:bg-surface/70"
                                }`
                            }
                        >
                            {label}
                            {label === "Cart" && count > 0 && (
                                <span className="absolute -top-1 -right-1 min-w-[1.25rem] h-5 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center px-1">
                                    {count}
                                </span>
                            )}
                        </NavLink>
                    ))}
                    <ContactSellerLink
                        className="ml-1 p-2 rounded-full text-muted-foreground hover:text-foreground hover:bg-surface/70 transition-colors"
                        iconClass="h-5 w-5"
                    />
                    <a
                        href="/how-to-order.html"
                        className="ml-1 p-2 rounded-full text-muted-foreground hover:text-foreground hover:bg-surface/70 transition-colors flex items-center gap-1.5 text-sm"
                        title="How to Order"
                    >
                        <BookOpen className="h-5 w-5" strokeWidth={1.5} />
                    </a>
                </nav>
            </div>
        </header>
    );
}
